/**
 * Manuals hub grouping: track (Operation / Reseller Commissioning) then topic.
 * Unmapped courses fall through to Other so they still appear.
 */

const TRACKS = [
  { id: 'user', label: 'Operation', defaultOpen: true },
  { id: 'advanced-commissioning', label: 'Reseller Commissioning', defaultOpen: false }
];

const TOPIC_ORDER = [
  'cell-ui',
  'pe320',
  'calibration-print',
  'robot',
  'scanner',
  'controller',
  'zeus-cnc',
  'other'
];

const TOPIC_LABELS = {
  'cell-ui': 'Cell & UI',
  pe320: 'PE320 & materials',
  'calibration-print': 'Calibration & first print',
  robot: 'Robot',
  scanner: 'Scanner',
  controller: 'Controller & I/O',
  'zeus-cnc': 'Zeus CNC',
  other: 'Other'
};

const COURSE_TOPIC = {
  'apollo-zeus-system-overview': 'cell-ui',
  'apollo-ui-user-guide': 'cell-ui',
  'apollo-ui-commissioning-operator': 'cell-ui',
  'apollo-ui-site-commissioning': 'cell-ui',

  'pe320-operator': 'pe320',
  'pe320-cold-start': 'pe320',
  'safety-cell-operation': 'pe320',
  'pe320-material-feed': 'pe320',
  'pe320-material-sensor-calibration': 'pe320',
  'pe320-preheat-soak': 'pe320',
  'pe320-purge': 'pe320',
  'pe320-nozzle-pellet-dryer-clean': 'pe320',
  'pe320-calibration': 'pe320',
  'extruder-servo-drive': 'pe320',

  'tcp-calibration': 'calibration-print',
  'ai-build-slice-and-print': 'calibration-print',
  'pellet-print-fault-finding': 'calibration-print',
  'pe320-drive-sto': 'pe320',
  'ai-build-advanced-slicing': 'calibration-print',

  'apollo-basic-maintenance': 'cell-ui',

  'kuka-operator-hmi': 'robot',
  'kuka-safe-operation': 'robot',
  'kuka-lubricate': 'robot',
  'kuka-directory-loader': 'robot',
  'abb-ethernet-ip': 'robot',
  'kuka-controller-hardware': 'robot',
  'kuka-ethercat-servo': 'robot',
  'kuka-ethercat-reconnect': 'robot',
  'kuka-workvisual-commissioning': 'robot',
  'kuka-robot-commissioning': 'robot',
  'kuka-workvisual-60-help': 'robot',
  'kuka-enable-additional-axes': 'robot',
  'kuka-load-data': 'robot',
  'kuka-safe-operation-vst': 'robot',
  'kuka-sys-x44-auto-restart': 'robot',
  'kuka-kl-4000-2': 'robot',
  'kuka-adaone-post': 'robot',
  'kuka-robot-mastering-faults': 'robot',

  'apollo-scanner-training': 'scanner',
  'apollo-scanner-offline-install': 'scanner',

  'epicurus-controller-io': 'controller',
  'duet-mainboard-sd': 'controller',

  'zeus-scan-to-mill': 'zeus-cnc',
  'kuka-tool-dock-cutter-change': 'zeus-cnc',
  'zeus-cnc-toolpath-generation': 'zeus-cnc',
  'zeus-cnc-maintenance': 'zeus-cnc',
  'spindle-vfd': 'zeus-cnc',
  'postprocessors': 'zeus-cnc'
};

function topicIdForCourse(course) {
  return COURSE_TOPIC[course.courseId] || 'other';
}

function groupCourses(courses) {
  return TRACKS.map((track) => {
    const trackCourses = (courses || []).filter((c) => (c.track || 'user') === track.id);
    const topics = TOPIC_ORDER.map((topicId) => {
      const list = trackCourses.filter((c) => topicIdForCourse(c) === topicId);
      if (!list.length) return null;
      return { id: topicId, label: TOPIC_LABELS[topicId], courses: list };
    }).filter(Boolean);
    return {
      id: track.id,
      label: track.label,
      defaultOpen: track.defaultOpen,
      count: trackCourses.length,
      topics
    };
  }).filter((track) => track.count > 0);
}

const GUIDE_SECTIONS = [
  { id: 'zero-to-first-print', title: 'Zero to first print' },
  { id: 'print-hero', title: 'Print hero' },
  { id: 'scan-to-mill', title: 'Scan to Mill' }
];

const HIDDEN_COURSES = new Set([
  'apollo-basic-maintenance',
  'ai-build-advanced-slicing',
  'zeus-cnc-toolpath-generation',
  'zeus-cnc-maintenance'
]);

function sectionIdFor(course, platformId) {
  if (!course || HIDDEN_COURSES.has(course.courseId)) return null;
  if ((course.track || 'user') === 'advanced-commissioning') return null;
  const zeus = platformId === 'zeus';
  if (course.courseId === 'kuka-lubricate') return zeus ? 'scan-to-mill' : null;
  if (course.courseId === 'apollo-scanner-training') return zeus ? 'scan-to-mill' : 'print-hero';
  if (course.phase === 'zero-to-first-print' || course.phase === 'print-hero') return course.phase;
  if (course.phase === 'zeus-addon' && zeus) return 'scan-to-mill';
  return null;
}

function byPathNumber(a, b) {
  const an = Number(a && a.pathNumber);
  const bn = Number(b && b.pathNumber);
  const aOk = Number.isFinite(an) && an > 0;
  const bOk = Number.isFinite(bn) && bn > 0;
  if (aOk && bOk && an !== bn) return an - bn;
  return 0;
}

function layoutGuides(courses, platformId) {
  const list = courses || [];
  if (platformId === 'sales') {
    const salesCourses = list.filter((course) => (course.track || '') === 'sales');
    return {
      phases: salesCourses.length
        ? [{ id: 'sales', title: 'Sales training', courses: salesCourses }]
        : [],
      rest: [],
      commissioning: []
    };
  }
  const commissioning = list
    .filter((course) => (course.track || 'user') === 'advanced-commissioning')
    .slice()
    .sort(byPathNumber);
  const phases = GUIDE_SECTIONS.map((section) => ({
    id: section.id,
    title: section.title,
    courses: list.filter((course) => sectionIdFor(course, platformId) === section.id).slice().sort(byPathNumber)
  })).filter((section) => section.courses.length);
  return { phases, rest: [], commissioning };
}

export { groupCourses, layoutGuides };
